/**
 * Карандаш суммы плеча: набить то, что уйдёт в чужую форму.
 *
 * После ввода число держится (замочек суммы): кэф уехал —
 * вторая нога и плюс едут за ним, набитое не затирается. Крестик или
 * «закрыл» на полке возвращают раскладку от банка. Закрытие карточки
 * замок не снимает, пока вилка в калькуляторе.
 */

import { useEffect, useRef, useState } from 'react';

export function AmountEdit({
  form,
  locked,
  onLock,
  onUnlock,
  children,
  armed = true,
  live = false,
  clickable = false,
  tail,
}: {
  form: string;
  locked: boolean;
  onLock: (form: string) => void;
  onUnlock: () => void;
  children: React.ReactNode;
  /** Карандаши и сброс. Выкл — только число: шапка карточки держит правку. */
  armed?: boolean;
  /** Режим правки шапки: поле открыто, ввод сразу в замок. */
  live?: boolean;
  /** Клик по самому числу, без карандаша и без смены вида в покое. */
  clickable?: boolean;
  /** Знак рядом с полем, чтобы `$` / `шер` не пропадали на время ввода. */
  tail?: string;
}): React.ReactElement {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(form);
  const field = useRef<HTMLInputElement>(null);
  /** Замок по blur — только если поле открыл карандаш ноги, не шапка. */
  const blurLock = useRef(false);
  // Печатают — не то же, что фокус: курсор стоит в поле и пока человек в окне
  // конторы, а пересчёт по новому кэфу туда идти обязан.
  const typed = useRef(false);

  useEffect(() => {
    if (live) blurLock.current = false;
    typed.current = false;
    setEdit(live);
  }, [live]);

  useEffect(() => {
    if (!edit) {
      setDraft(form);
      return;
    }
    // Живой режим: чужой замок (лента, соседняя нога) должен попасть в поле,
    // пока в него не печатают. Иначе `setOddsQuote` снаружи оставляет старый ввод.
    if (live && !typed.current) setDraft(form);
  }, [edit, form, live]);

  useEffect(() => {
    if (!edit || live) return;
    const node = field.current;
    if (node === null) return;
    node.focus();
    node.select();
  }, [edit, live]);

  const parsed = (value: string): string | null => {
    const raw = value.trim().replace(/\s/g, '').replace(',', '.');
    if (raw === '' || raw === '.') return null;
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) && n > 0 ? raw : null;
  };

  const commit = (value: string, emptyUnlock: boolean): boolean => {
    const raw = value.trim().replace(/\s/g, '').replace(',', '.');
    if (raw === '') {
      if (emptyUnlock) onUnlock();
      return emptyUnlock;
    }
    const next = parsed(value);
    if (next === null) return false;
    onLock(next);
    return true;
  };

  const start = (event: React.MouseEvent): void => {
    if (!clickable || edit || live) return;
    event.stopPropagation();
    blurLock.current = true;
    setDraft(form);
    setEdit(true);
  };

  return (
    <span
      className={`nx-amt${locked ? ' own' : ''}${edit ? ' edit' : ''}${clickable && !edit ? ' hit' : ''}`}
      onClick={start}
    >
      {edit ? (
        <>
        <input
          ref={field}
          className="nx-amt__in"
          value={draft}
          inputMode="decimal"
          aria-label="сумма плеча"
          onChange={(event) => {
            const value = event.target.value;
            typed.current = true;
            setDraft(value);
            commit(value, false);
          }}
          onBlur={() => {
            typed.current = false;
            const value = field.current?.value ?? draft;
            if (!blurLock.current) {
              if (value.trim() === '') onUnlock();
              else setDraft(form);
              return;
            }
            if (!commit(value, true)) setDraft(form);
            setEdit(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.blur();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              typed.current = false;
              setDraft(form);
              if (!live) setEdit(false);
            }
            event.stopPropagation();
          }}
          onClick={(event) => event.stopPropagation()}
        />
        {clickable && tail ? <b className="nx-amt__tail">{tail}</b> : null}
        </>
      ) : (
        children
      )}
      {armed && !live && (
      <button
        type="button"
        className="nx-amt__pen"
        title={edit ? 'готово' : 'править сумму плеча'}
        aria-label="править сумму"
        onClick={(event) => {
          event.stopPropagation();
            if (edit) {
            if (!commit(draft, true)) setDraft(form);
            setEdit(false);
            return;
          }
          setDraft(form);
          blurLock.current = true;
          setEdit(true);
        }}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M11.6 2.6 13.4 4.4 6.2 11.6 4 12l.4-2.2z" />
          <path d="M10.4 3.8 12.2 5.6" />
        </svg>
      </button>
      )}
      {armed && locked && (!edit || live) && (
        <button
          type="button"
          className="nx-amt__off"
          title="снова от банка"
          aria-label="сбросить свою сумму"
          onClick={(event) => {
            event.stopPropagation();
            onUnlock();
          }}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M4.5 4.5 11.5 11.5M11.5 4.5 4.5 11.5" />
          </svg>
        </button>
      )}
    </span>
  );
}
